import { formatGold } from '@/helpers/helpers';
import { toBcp47Locale } from '@/helpers/languageConstants';
import { MomentumData, TokenData } from '@/hooks/useWoWToken';
import React, { useId, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import wow_logo from "../../assets/wow.svg";

interface WoWTokenDisplayProps {
    tokenData: TokenData | null;
    loading: boolean;
    error: string | null;
    showIcon?: boolean;
    className?: string;
}

interface DayBoundary {
    x: number;
    label: string;
}

// ─── Momentum Indicator ──────────────────────────────────────────────────────

const MomentumBar: React.FC<{ percentage: number; direction: 'up' | 'down' | 'flat' }> = ({ percentage, direction }) => {
    const clamped = Math.max(0, Math.min(100, percentage));
    const segments = 10;
    const filled = Math.round(clamped / segments);

    const barColor = direction === 'down'
        ? 'bg-red-400'
        : direction === 'up'
            ? 'bg-green-400'
            : 'bg-gray-400';

    const barTrack = 'bg-fp-border/50';

    return (
        <div className="flex items-center gap-0.5">
            {Array.from({ length: segments }).map((_, i) => (
                <div
                    key={i}
                    className={`w-1.5 h-2.5 rounded-[1px] transition-colors duration-300 ${i < filled ? barColor : barTrack}`}
                />
            ))}
            <span className="text-[10px] text-gray-400 ml-1 tabular-nums">{Math.round(clamped)}%</span>
        </div>
    );
};

const SIGNAL_CONFIG = {
    BUY: {
        label: 'BUY',
        titleKey: 'common:tokenDisplay.signalBuy',
        color: 'text-emerald-400',
        bg: 'bg-emerald-400/10',
        border: 'border-emerald-400/30',
        dot: 'bg-emerald-400',
        glow: 'shadow-[0_0_6px_rgba(52,211,153,0.3)]',
    },
    WAIT: {
        label: 'WAIT',
        titleKey: 'common:tokenDisplay.signalWait',
        color: 'text-red-400',
        bg: 'bg-red-400/10',
        border: 'border-red-400/30',
        dot: 'bg-red-400',
        glow: 'shadow-[0_0_6px_rgba(248,113,113,0.3)]',
    },
    HOLD: {
        label: 'HOLD',
        titleKey: 'common:tokenDisplay.signalHold',
        color: 'text-amber-400',
        bg: 'bg-amber-400/10',
        border: 'border-amber-400/30',
        dot: 'bg-amber-400',
        glow: 'shadow-[0_0_6px_rgba(251,191,36,0.3)]',
    },
    UNKNOWN: {
        label: '...',
        titleKey: null,
        color: 'text-gray-500',
        bg: 'bg-gray-500/10',
        border: 'border-gray-500/30',
        dot: 'bg-gray-500',
        glow: '',
    },
} as const;

const SignalBadge: React.FC<{ signal: MomentumData['signal'] }> = ({ signal }) => {
    const { t } = useTranslation(['common']);
    const config = SIGNAL_CONFIG[signal];
    return (
        <span title={config.titleKey ? t(config.titleKey) : undefined} className={`
            inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase
            border ${config.bg} ${config.border} ${config.color} ${config.glow}
        `}>
            <span className={`w-1.5 h-1.5 rounded-full ${config.dot} ${signal !== 'UNKNOWN' ? 'animate-pulse' : ''}`} />
            {config.label}
        </span>
    );
};

const DirectionArrow: React.FC<{ direction: 'up' | 'down' | 'flat'; decelerating: boolean }> = ({ direction, decelerating }) => {
    if (direction === 'up') {
        return decelerating
            ? <span className="text-green-400 text-xs">↗</span>
            : <span className="text-green-400 text-xs">⬆</span>;
    }
    if (direction === 'down') {
        return decelerating
            ? <span className="text-red-400 text-xs">↘</span>
            : <span className="text-red-400 text-xs">⬇</span>;
    }
    return <span className="text-gray-400 text-xs">→</span>;
};

const MomentumPanel: React.FC<{ momentum: MomentumData }> = ({ momentum }) => {
    const { t } = useTranslation(['common']);
    const config = SIGNAL_CONFIG[momentum.signal];

    return (
        <div className={`mt-2 pt-2 border-t border-fp-border/60`}>
            {/* Signal row */}
            <div className="flex items-center justify-between mb-1.5">
                <SignalBadge signal={momentum.signal} />
                <div className="flex items-center gap-1.5">
                    <DirectionArrow direction={momentum.direction} decelerating={momentum.decelerating} />
                    <span className="text-[10px] text-gray-500">
                        {momentum.runLength !== 1 ? t('common:tokenDisplay.stepsPlural', { count: momentum.runLength }) : t('common:tokenDisplay.steps', { count: momentum.runLength })}
                    </span>
                </div>
            </div>

            {/* Momentum bar */}
            <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-gray-500">{t('common:tokenDisplay.momentum')}</span>
                <MomentumBar percentage={momentum.momentum} direction={momentum.direction} />
            </div>

            {/* Status indicators */}
            <div className="flex items-center gap-2 mb-1.5">
                {momentum.decelerating && (
                    <span className="text-[10px] text-amber-400/80 flex items-center gap-0.5">
                        <span className="w-1 h-1 rounded-full bg-amber-400/80" />
                        {t('common:tokenDisplay.slowing', { steps: momentum.decelSteps })}
                    </span>
                )}
                {momentum.atCap && (
                    <span className="text-[10px] text-red-400/80 flex items-center gap-0.5">
                        <span className="w-1 h-1 rounded-full bg-red-400/80" />
                        {t('common:tokenDisplay.atCap')}
                    </span>
                )}
            </div>

            {/* Reason */}
            <p className={`text-[10px] leading-tight ${config.color} opacity-80`}>
                {momentum.reason}
            </p>
        </div>
    );
};

// ─── Inline Signal Pill (shown next to price in header bar) ──────────────────

const InlineSignalPill: React.FC<{ signal: MomentumData['signal'] }> = ({ signal }) => {
    const { t } = useTranslation(['common']);
    if (signal === 'UNKNOWN') return null;
    const config = SIGNAL_CONFIG[signal];
    return (
        <span title={t(config.titleKey)} className={`
            inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider uppercase leading-none
            border ${config.bg} ${config.border} ${config.color}
        `}>
            <span className={`w-1 h-1 rounded-full ${config.dot} animate-pulse`} />
            {config.label}
        </span>
    );
};

// ─── Mini Chart ──────────────────────────────────────────────────────────────

const MiniChart: React.FC<{ data: Array<{ timestamp: number, price: number }>, uniqueId: string, weekdays: string[] }> = ({ data, uniqueId, weekdays }) => {
    const width = 300;
    const height = 100;
    const padding = { top: 10, right: 10, bottom: 25, left: 10 };

    if (data.length < 2) return null;

    const prices = data.map(d => d.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice;

    const verticalPadding = priceRange * 0.1 || 1;
    const adjustedMin = minPrice - verticalPadding;
    const adjustedMax = maxPrice + verticalPadding;
    const adjustedRange = adjustedMax - adjustedMin;

    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Calculate points
    const points = data.map((point, index) => {
        const x = (index / (data.length - 1)) * chartWidth + padding.left;
        const y = padding.top + chartHeight - ((point.price - adjustedMin) / adjustedRange) * chartHeight;
        return { x, y, timestamp: point.timestamp };
    });

    const pointsString = points.map(p => `${p.x},${p.y}`).join(' ');

    // Determine line color based on overall trend
    const firstPrice = data[0].price;
    const lastPrice = data[data.length - 1].price;
    const lineColor = lastPrice > firstPrice ? '#10b981' : lastPrice < firstPrice ? '#f87171' : '#9ca3af';

    // Calculate day boundaries for vertical lines
    const dayBoundaries: DayBoundary[] = [];
    const dayLabels: { x: number; label: string }[] = [];

    let lastDateStr: string | null = null;

    data.forEach((point, index) => {
        const date = new Date(point.timestamp * 1000);
        const dateStr = date.toDateString();

        if (lastDateStr !== null && dateStr !== lastDateStr) {
            const x = (index / (data.length - 1)) * chartWidth + padding.left;
            dayBoundaries.push({ x, label: weekdays[date.getDay()] });
        }

        lastDateStr = dateStr;
    });

    // Build day labels - place them in the CENTER of each day's data span
    if (data.length > 0) {
        const dayRanges: { startX: number; endX: number; label: string }[] = [];
        let currentDayStart = 0;
        let currentDateStr = new Date(data[0].timestamp * 1000).toDateString();
        let currentDayOfWeek = new Date(data[0].timestamp * 1000).getDay();

        data.forEach((point, index) => {
            const date = new Date(point.timestamp * 1000);
            const dateStr = date.toDateString();

            if (dateStr !== currentDateStr) {
                const startX = (currentDayStart / (data.length - 1)) * chartWidth + padding.left;
                const endX = ((index - 1) / (data.length - 1)) * chartWidth + padding.left;
                dayRanges.push({ startX, endX, label: weekdays[currentDayOfWeek] });

                currentDayStart = index;
                currentDateStr = dateStr;
                currentDayOfWeek = date.getDay();
            }
        });

        // Don't forget the last day
        const startX = (currentDayStart / (data.length - 1)) * chartWidth + padding.left;
        const endX = chartWidth + padding.left;
        dayRanges.push({ startX, endX, label: weekdays[currentDayOfWeek] });

        // Place labels at the center of each day's range
        const minLabelSpacing = 30;
        let lastLabelX = -Infinity;

        dayRanges.forEach(range => {
            const centerX = (range.startX + range.endX) / 2;

            if (centerX - lastLabelX >= minLabelSpacing) {
                dayLabels.push({ x: centerX, label: range.label });
                lastLabelX = centerX;
            }
        });
    }

    // Unique IDs to prevent conflicts with multiple charts
    const gradientId = `token-chart-gradient-${uniqueId}`;
    const gridId = `token-chart-grid-${uniqueId}`;

    return (
        <svg width={width} height={height} className="block">
            <defs>
                <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor={lineColor} stopOpacity="0.3" />
                    <stop offset="100%" stopColor={lineColor} stopOpacity="0.05" />
                </linearGradient>
                <pattern id={gridId} width="20" height="16" patternUnits="userSpaceOnUse">
                    <path d="M 20 0 L 0 0 0 16" fill="none" stroke="#374151" strokeWidth="0.5" opacity="0.3" />
                </pattern>
            </defs>

            {/* Background grid */}
            <rect x={padding.left} y={padding.top} width={chartWidth} height={chartHeight} fill={`url(#${gridId})`} />

            {/* Day boundary vertical lines */}
            {dayBoundaries.map((boundary, index) => (
                <line
                    key={`boundary-${index}`}
                    x1={boundary.x}
                    y1={padding.top}
                    x2={boundary.x}
                    y2={padding.top + chartHeight}
                    stroke="#ffffff"
                    strokeWidth="1"
                    opacity="0.3"
                />
            ))}

            {/* Area fill */}
            <path
                d={`M ${padding.left},${padding.top + chartHeight} L ${pointsString} L ${width - padding.right},${padding.top + chartHeight} Z`}
                fill={`url(#${gradientId})`}
            />

            {/* Price line */}
            <polyline
                fill="none"
                stroke={lineColor}
                strokeWidth="2"
                points={pointsString}
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            {/* Data points */}
            {points.map((point, index) => (
                <circle
                    key={index}
                    cx={point.x}
                    cy={point.y}
                    r="2.5"
                    fill={lineColor}
                    className="opacity-60 hover:opacity-100 transition-opacity"
                />
            ))}

            {/* X-axis line */}
            <line
                x1={padding.left}
                y1={padding.top + chartHeight}
                x2={width - padding.right}
                y2={padding.top + chartHeight}
                stroke="#4b5563"
                strokeWidth="1"
            />

            {/* Day labels on x-axis */}
            {dayLabels.map((label, index) => (
                <text
                    key={`label-${index}`}
                    x={label.x}
                    y={height - 5}
                    fill="#9ca3af"
                    fontSize="10"
                    textAnchor="middle"
                >
                    {label.label}
                </text>
            ))}
        </svg>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────

export const WoWTokenDisplay: React.FC<WoWTokenDisplayProps> = ({
    tokenData,
    loading,
    error,
    showIcon = true,
    className = "flex items-center font-semibold text-white space-x-2"
}) => {
    const { t, i18n } = useTranslation(['common']);
    const uniqueId = useId();
    const [showChart, setShowChart] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout>(null);

    // Localized short weekday names, Sunday-first (index 0 = Sunday, matching Date#getDay).
    const weekdays = useMemo(() => {
        const formatter = new Intl.DateTimeFormat(toBcp47Locale(i18n.language), { weekday: 'short', timeZone: 'UTC' });
        // 2023-01-01 was a Sunday; step through one full week to build the lookup.
        return Array.from({ length: 7 }, (_, i) => formatter.format(new Date(Date.UTC(2023, 0, 1 + i))));
    }, [i18n.language]);

    const handleMouseEnter = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setShowChart(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setShowChart(false);
        }, 200);
    };

    if (loading && !tokenData) {
        return (
            <div className={className}>
                {showIcon && <img src={wow_logo} width={25} height="auto" className="mr-1" />}
                {t('common:tokenDisplay.label')}: {t('common:tokenDisplay.loading')}
            </div>
        );
    }

    if (error && !tokenData) {
        return (
            <div className={`${className.replace('text-white', 'text-red-400')}`}>
                {showIcon && <img src={wow_logo} width={25} height="auto" className="mr-1" />}
                {t('common:tokenDisplay.label')}: {t('common:tokenDisplay.error')}
            </div>
        );
    }

    if (!tokenData) return null;

    const { price, changePercentage, historicalData, momentum } = tokenData;

    let changeColor = "text-gray-400";
    let ChangeIcon = Minus;

    if (changePercentage !== null) {
        if (changePercentage > 0) {
            changeColor = "text-green-400";
            ChangeIcon = ArrowUp;
        } else if (changePercentage < 0) {
            changeColor = "text-red-400";
            ChangeIcon = ArrowDown;
        }
    }

    return (
        <div className="relative">
            <div
                className={className}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {showIcon && <img src={wow_logo} width={25} height="auto" className="mr-1" />}
                <span className="text-sm cursor-default">{t('common:tokenDisplay.label')}: {formatGold(price)}</span>
                {changePercentage !== null && (
                    <div className={`flex items-center space-x-1 ${changeColor}`}>
                        <ChangeIcon className="w-3 h-3" />
                        <span className="text-xs">
                            {Math.abs(changePercentage).toFixed(2)}%
                        </span>
                    </div>
                )}
                {/* Inline signal pill next to the price */}
                {momentum && momentum.signal !== 'UNKNOWN' && (
                    <InlineSignalPill signal={momentum.signal} />
                )}
                {loading && (
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                )}
            </div>

            {/* Chart tooltip */}
            {showChart && historicalData && historicalData.length > 1 && (
                <div
                    className="absolute top-full mt-2 left-0 bg-fp-bg border border-fp-border rounded-lg p-3 shadow-xl z-50 min-w-105"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <div className="text-[11px] font-semibold text-fp-text2 uppercase tracking-[.07em] mb-2">{t('common:tokenDisplay.priceHistory')}</div>
                    <div className="flex items-start space-x-4">
                        <div className="flex-1">
                            <MiniChart data={historicalData} uniqueId={uniqueId} weekdays={weekdays} />
                        </div>
                        <div className="text-xs text-gray-400 space-y-1 min-w-22.5">
                            <div className="flex justify-between">
                                <span className="text-gray-500">{t('common:tokenDisplay.high')}</span>
                                <span className="text-green-400 font-medium">
                                    {formatGold(Math.max(...historicalData.map(d => d.price)))}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">{t('common:tokenDisplay.low')}</span>
                                <span className="text-red-400 font-medium">
                                    {formatGold(Math.min(...historicalData.map(d => d.price)))}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">{t('common:tokenDisplay.avg')}</span>
                                <span className="text-gray-300 font-medium">
                                    {formatGold(historicalData.reduce((sum, d) => sum + d.price, 0) / historicalData.length)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Momentum analysis panel */}
                    {momentum && momentum.signal !== 'UNKNOWN' && (
                        <MomentumPanel momentum={momentum} />
                    )}
                </div>
            )}
        </div>
    );
};